import React, { useState } from 'react';
import { IntlShape } from 'react-intl';
import { observer } from 'mobx-react';
import cn from 'classnames';

import ChevronSVG from '../../../../../../../../images/svg/icons8-expand-arrow';
import { FormField } from '../../../../../../../global/form/form-field/form-field';
import { OEToggle } from '../../../../../../../global/oe-toggle/oe-toggle';
import { RichTextEditor } from '../../../../../../../global/rich-text-editor';
import Text from '../../../../../../../global/text/text';
import GeneralRegistrationVM from '../general-registration.viewmodel';
import { IRegistrationSettings, IRegistrationType, RegistrationTypeValue } from '../../../../../../../../api/registration-configuration.dao';

interface IGeneralRegistrationSettingsItem {
    vm: GeneralRegistrationVM;
    intl: IntlShape;
    triageAnswer: string;
    setting: IRegistrationSettings | undefined;
    registrationType: IRegistrationType[];
}

const GeneralRegistrationSettingsItem = observer(({
    vm,
    intl,
    triageAnswer,
    setting,
    registrationType,
}: IGeneralRegistrationSettingsItem) => {
    const [isOpen, setIsOpen] = useState(false);
    const isSettingExist = !!setting;
    const isDefault = triageAnswer === '';
    const settingTitle = isDefault ? 'Default Settings' : triageAnswer;

    return (
        <div className='general-settings-item' data-setting-id={settingTitle}>
            <div
                onClick={() => setIsOpen(prev => !prev)}
                className={cn('general-settings-item__header', {
                    'general-settings-item__header--active': isOpen,
                })}
            >
                <div className='general-settings-item__header-name'>
                    <div className='general-settings-item__header-title'>
                        <Text
                            message={settingTitle}
                            withEllipsis
                        />
                    </div>
                    <div className='general-settings-item__header-icon'>
                        <ChevronSVG/>
                    </div>
                </div>
            </div>

            {isOpen &&
            <div
                className='general-settings-item__content'
            >

                {!isDefault &&
                    <div
                        className='website-custom__checkbox-label m-top-15px m-bottom-20px'
                    >
                        <OEToggle
                            isSelected={isSettingExist}
                            onClick={() => vm.toggleTriageAnswerWithinSettings(triageAnswer)}
                        />
                        <Text
                            message={`${isSettingExist
                                ? 'Custom Settings is enabled'
                                : 'Custom Settings is disabled'
                            }`}
                            id={`${isSettingExist
                                ? 'oe.triageQuestion.registrationSettings.settingEnabled'
                                : 'oe.triageQuestion.registrationSettings.settingDisabled'
                            }`}
                            description={'Label text'}
                        />
                    </div>
                }

                {isSettingExist && <>
                    <div className='website-custom__checkbox-label column m-top-15px m-bottom-0px'>
                        <Text
                            message='Registration Type'
                            id='oe.website.registrationType'
                            description='Label text'
                        />
                        <select
                            className='oe-form-field__control'
                            value={vm.getRegistrationType(triageAnswer)}
                            onChange={e => vm.setRegistrationType(e.target.value as RegistrationTypeValue, triageAnswer)}
                            disabled={isDefault && !vm.registrationAttendeeEnabled}
                        >
                            {
                                registrationType.map(({ value, textInUI }) => <option value={value}
                                    key={value}>{textInUI}</option>)
                            }
                        </select>
                    </div>
                    <div className='website-custom__checkbox-label m-top-15px m-bottom-0px'>
                        <OEToggle
                            isSelected={vm.getCalendarButton(triageAnswer)}
                            onClick={() => vm.toggleCalendarButtonLabel(triageAnswer)}
                            isDisabled={isDefault && !vm.registrationAttendeeEnabled}
                        />
                        <Text
                            message={'Show "Add to Calendar" button'}
                            id={'oe.website.showAddToCalendarButton'}
                            description={'Label text'}
                        />
                    </div>
                    <div className='website-custom__checkbox-label m-top-15px m-bottom-0px'>
                        <OEToggle
                            data-testid='auto-sign-in'
                            isSelected={vm.getAutoSignInEnabled(triageAnswer)}
                            onClick={() => vm.toggleAutoSignIn(triageAnswer)}
                            isDisabled={isDefault && !vm.getRegistrationAttendeeEnabled()}
                        />
                        <Text
                            message='One-Step Registration and Sign In'
                            id='oe.website.autoSignIn'
                            description='Label text'
                        />
                    </div>
                    <span className='oe-form-field__info m-bottom-15px'>
                        {!vm.isRegistrationTypeOpen(triageAnswer)
                            ? intl.formatMessage({
                                id: 'oe.website.registrationType.CONTROLLED.info',
                                description: 'Input info text',
                                defaultMessage: 'Turn on to allow attendees who successfully register and belong to an approved domain or are already approved to automatically be logged into Passport.'
                            })
                            : intl.formatMessage({
                                id: 'oe.website.autoSignIn.info',
                                description: 'Input info text',
                                defaultMessage: 'Attendees who successfully register will automatically be logged in to Passport'
                            })
                        }
                    </span>
                    <div className='website-custom__checkbox-label m-bottom-15px'>
                        <OEToggle
                            data-testid='show-open-meetings'
                            isSelected={vm.getShowOpenMeetingsEnabled(triageAnswer)}
                            onClick={() => vm.toggleShowOpenMeetings(triageAnswer)}
                            isDisabled={isDefault && !vm.registrationAttendeeEnabled}
                        />
                        <Text
                            message='Show Open meetings on Registration Page'
                            id='oe.website.autoSignIn'
                            description='Label text'
                        />
                    </div>
                    <FormField
                        label={intl.formatMessage({
                            defaultMessage: 'Registration Header',
                            id: 'oe.website.registrationHeader',
                            description: 'Label text'
                        })}
                    >
                        <input
                            className='oe-form-field__control'
                            value={setting.header}
                            type='text'
                            onChange={({ target: { value } }) => vm.setRegistrationHeaderMessage(value, triageAnswer)}
                        />
                        {!!vm.getErrorMessage(triageAnswer, 'header') &&
                            <span
                                className='oe-form-field__error'>{vm.getErrorMessage(triageAnswer, 'header')}</span>
                        }

                    </FormField>
                    <FormField
                        label={intl.formatMessage({
                            defaultMessage: 'Registration Instructions',
                            id: 'oe.website.registrationInstructions',
                            description: 'Label text'
                        })}
                        className='website-custom__editor'
                    >
                        <RichTextEditor
                            value={setting.instructions}
                            onChange={(event, editor) => vm.setRegistrationInstructionsMessage(editor.getData(), triageAnswer)}
                            validationError={vm.getErrorMessage(triageAnswer, 'instructions') || null}
                        />
                    </FormField>
                    <FormField
                        label={intl.formatMessage({
                            defaultMessage: 'Registration Submitted Message',
                            id: 'oe.registration.submitMessage',
                            description: 'Label text'
                        })}
                        className='website-custom__editor'
                    >
                        <RichTextEditor
                            value={vm.getRegistrationSubmitMessage(triageAnswer)}
                            onChange={(event, editor) => vm.setRegistrationSubmitMessage(editor.getData(), triageAnswer)}
                            validationError={vm.getErrorMessage(triageAnswer, 'registrationSubmittedMessage') || null}
                        />
                    </FormField>
                    <FormField
                        label={intl.formatMessage({
                            defaultMessage: 'Registration Pending & Denied Message',
                            id: 'oe.registration.pendingDeniedMessage',
                            description: 'Label text'
                        })}
                        className='website-custom__editor'
                        infoIconText={intl.formatMessage({
                            id: 'oe.registration.pendingDeniedMessage.info',
                            defaultMessage: 'This message will be displayed to attendees when their registration is pending or denied.',
                            description: 'Info text'
                        })}
                    >
                        <RichTextEditor
                            value={vm.getPendingDeniedMessage(triageAnswer)}
                            onChange={(event, editor) => vm.setPendingDeniedMessage(editor.getData(), triageAnswer)}
                            validationError={vm.getErrorMessage(triageAnswer, 'pendingDeniedMessage') || null}
                        />
                    </FormField>
                </>}
            </div>}
        </div>
    );
});

export default GeneralRegistrationSettingsItem;
